import TooltipIcon from '@/components/LayoutTools/Sidebar/TooltipIcon';
import InactiveTableConnectorIcon from '@/public/images/ic-table-connector_inactive.svg';
import AdministrationIcon from '@/public/images/Icon-Administration.svg';
import DataManagementIcon from '@/public/images/icon-data-management.svg';
import EDAIcon from '@/public/images/Icon-EDA.svg';
import HomeIcon from '@/public/images/Icon-Home.svg';
import iBankReportingIcon from '@/public/images/Icon-Reporting.svg';
import SpendManagementIcon from '@/public/images/icon-spend-management.svg';
import TravelManagement from '@/public/images/Icon-Travel-Management.svg';
import TravelSpendIcon from '@/public/images/icon-travel-spend-management.svg';
import { hasPermission } from '@/utils/hubPermissions/hasPermissions';
import { isPermissionedRoute } from '@/utils/routing/routing';
import { ManageAccounts } from '@mui/icons-material';
import BarChartIcon from '@mui/icons-material/BarChart';
import React from 'react';

const navigationItems = [
  {
    label: 'Home',
    icon: <TooltipIcon src={HomeIcon} alt='Home' />,
    url: '/hub',
  },

  // Travel Management Drawer.
  {
    label: 'Travel Management',
    icon: <TooltipIcon src={TravelManagement} alt='Travel Management' />,
    isDrawer: true,
    items: [
      {
        label: 'QC, Mail, Ticketing',
        permission: 'qcMailTicketing',
        items: [
          {
            label: 'Schedules',
            url: '/travel-management/qc-mail-ticketing/schedules',
          },
          {
            label: 'Tables',
            url: '/travel-management/qc-mail-ticketing/tables',
          },
          {
            label: 'ResRules',
            url: '/travel-management/qc-mail-ticketing/resrules',
          },
          {
            label: 'Operations',
            url: '/travel-management/qc-mail-ticketing/operations',
          },
          {
            label: 'Data Insights',
            url: '/travel-management/qc-mail-ticketing/data-insights',
          },
          {
            label: 'Demo - Reporting and Analytics',
            url: '/travel-management/qc-mail-ticketing/demo-reporting-and-analytics',
          },
        ],
      },
      {
        label: 'Queue Monitor',
        url: '/travel-management/queue-monitor',
      },
      {
        label: 'TicketTRAK™',
        url: '/travel-management/tickettrak',
      },
      {
        label: 'Travel Authorization',
        permission: 'travelAuthorization',
        items: [
          {
            label: 'Pending Requests',
            url: '/travel-management/travel-authorization/pending-requests',
          },
          {
            label: 'Search Requests',
            url: '/travel-management/travel-authorization/search-requests',
          },
          {
            label: 'Travel Policy',
            url: '/travel-management/travel-authorization/travel-policy',
          },
          {
            label: 'Meetings',
            url: '/travel-management/travel-authorization/meetings',
          },
          {
            label: 'Business Insights',
            url: '/travel-management/travel-authorization/business-insights',
          },
          {
            label: 'Transaction Logs',
            url: '/travel-management/travel-authorization/transaction-logs',
          },
          {
            label: 'Document Logs',
            url: '/travel-management/travel-authorization/document-logs',
          },
        ],
      },
      {
        label: 'Reports',
        permission: 'reports',
        items: [
          {
            label: 'Travel',
            url: '/travel-management/reports/travel',
          },
          {
            label: 'Operations',
            url: '/travel-management/reports/operations',
          },
          {
            label: 'iBank',
            url: '/travel-management/reports/ibank',
          },
        ],
      },
    ],
  },
  {
    label: 'Spend Management',
    icon: <TooltipIcon src={SpendManagementIcon} alt='Travel + Spend' />,
    url: '/spend-management',
  },
  {
    label: 'Travel + Spend',
    icon: <TooltipIcon src={TravelSpendIcon} alt='Spend Management' />,
    url: '/travel-spend',
  },
  {
    label: 'Data Management',
    icon: <TooltipIcon src={DataManagementIcon} alt='Data Management' />,
    url: '/data-management',
  },
  {
    label: 'QuickSight Proof of Concept',
    icon: <TooltipIcon Icon={BarChartIcon} alt='QuickSight Proof of Concept' />,
    url: '/quicksight',
  },
  {
    label: 'Table Connector',
    icon: (
      <TooltipIcon src={InactiveTableConnectorIcon} alt='Table Connector' />
    ),
    url: '/table-connector',
  },
  {
    label: 'Event Driven Actions',
    icon: <TooltipIcon src={EDAIcon} alt='EDA' />,
    url: '/event-driven-actions',
  },
  {
    label: 'iBank Reporting',
    icon: <TooltipIcon src={iBankReportingIcon} alt='iBank Reporting' />,
    permission: 'ibankReporting',
    items: [
      {
        label: 'Log in to iBank',
        url: '/ibank-reporting/login',
      },
      {
        label: 'Event Logs',
        url: '/ibank-reporting/event-logs',
      },
      {
        label: 'Reports',
        url: '/ibank-reporting/reports',
      },
    ],
  },
  {
    label: 'Administration',
    icon: <TooltipIcon src={AdministrationIcon} alt='Administration' />,
    permission: 'administration',
    items: [
      {
        label: 'Site Configuration',
        url: '/administration/site-configuration',
      },
      {
        label: 'Asset Management',
        url: '/administration/asset-management',
      },
      {
        label: 'User Management',
        url: '/administration/user-management',
      },
      {
        label: 'Role Management',
        url: '/administration/role-management',
      },
      {
        label: 'Activity Logs',
        url: '/administration/activity-log',
      },
    ],
  },
  {
    label: 'CIS Internal Tools',
    icon: <TooltipIcon Icon={ManageAccounts} alt='CIS Internal Tools' />,
    permission: 'cisInternal',
    items: [
      {
        label: 'Organizations',
        url: '/cis-internal-tools/organizations',
      },
      {
        label: 'Libraries',
        url: '/cis-internal-tools/libraries',
      },
      {
        label: 'Schedule Mover',
        url: '/cis-internal-tools/schedule-mover',
      },
      {
        label: 'Engine Monitor',
        url: '/cis-internal-tools/engine-monitor',
      },
      {
        label: 'Queue Monitor',
        url: '/cis-internal-tools/queue-monitor',
      },
      {
        label: 'iQCX Alert Notifications',
        url: '/cis-internal-tools/iqcx-alert-notifications',
      },
      {
        label: 'Email Notifications',
        url: '/cis-internal-tools/email-notification',
      },
      {
        label: 'Dynamic Reporting',
        url: '/cis-internal-tools/dynamic-reporting',
      },
      {
        label: 'GTR Stats',
        url: '/cis-internal-tools/gtr-stats',
      },
      {
        label: 'Feature Flag Management',
        url: '/cis-internal-tools/feature-flag-management',
      },
    ],
  },
];

export default navigationItems;

export function getAllNavigationItems() {
  let allItems = [];
  navigationItems.forEach((item) => {
    allItems.push(item);
    if (item.items) {
      item.items.forEach((subItem) => {
        allItems.push(subItem);
      });
    }
  });
  return allItems;
}

function hasAccess(item, user, org) {
  if (item.isDrawer) {
    return item.items.some((subItem) => hasAccess(subItem, user, org));
  }
  if (!item.url) {
    return (
      hasPermission(user, item.permission) &&
      hasPermission(org, item.permission)
    );
  }
  return isPermissionedRoute(item.url, user, org);
}

function getItemsWithAccess(items, user, org) {
  return items.filter((item) => hasAccess(item, user, org));
}

export function filterNavItems(user, org) {
  const results = getItemsWithAccess(navigationItems, user, org);
  for (let item of results) {
    if (item.items) {
      item.items = getItemsWithAccess(item.items, user, org);
      for (let subItem of item.items) {
        if (subItem.items) {
          subItem.items = getItemsWithAccess(subItem.items, user, org);
        }
      }
    }
  }

  return results;
}
